import { useMemo, useState } from 'react';
import { useUserInvoices } from '../../../hooks/useUserInvoices';
import type { UnifiedInvoice, UserInvoiceFilters } from '../../../lib/userInvoicesApi';
import { UnifiedInvoicesFilters } from './invoices/UnifiedInvoicesFilters';
import { UnifiedInvoicesTable } from './invoices/UnifiedInvoicesTable';
import { InvoicePreviewModal } from './invoices/InvoicePreviewModal';

export interface PersonInvoicesTabProps {
  /** Person.id — used for the SmartBill summary fallback (accountless people). */
  personId: string;
  /** Person.userId — drives the unified per-user invoices view (CLE-238). */
  userId: string | null;
}

const PAGE_SIZE = 25;

function formatMoney(amount: number): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RON`;
}

function SummaryStat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${accent ?? 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

/**
 * Invoices tab for the Team Member detail page (CLE-238).
 *
 * Shows a unified, paginated, filterable view of every invoice associated with
 * the person across all sources (SmartBill, manual upload, email ingest), with
 * a financial summary and PDF preview on row click. People without a linked
 * login account can't be resolved by the per-user endpoint, so we fall back to
 * a plain empty state (the SmartBill card that used to sit here is hidden).
 */
export function PersonInvoicesTab({ userId }: PersonInvoicesTabProps) {
  const [filters, setFilters] = useState<UserInvoiceFilters>({ page: 1, limit: PAGE_SIZE });
  const [preview, setPreview] = useState<UnifiedInvoice | null>(null);

  const { data, isLoading, isError } = useUserInvoices(userId ?? undefined, filters);

  // Distinct category/status labels from the current page feed the dropdowns.
  const categories = useMemo(
    () =>
      Array.from(
        new Set((data?.invoices ?? []).map((i) => i.category).filter((c): c is string => !!c)),
      ).sort(),
    [data],
  );
  const statuses = useMemo(
    () => Array.from(new Set((data?.invoices ?? []).map((i) => i.status))).sort(),
    [data],
  );

  // --- Accountless people have no unified invoices view yet ------------------
  if (!userId) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-sm">
        Nicio factură asociată acestei persoane încă. Asociază un cont de autentificare pentru a
        vedea vizualizarea unificată per sursă.
      </div>
    );
  }

  const patch = (p: Partial<UserInvoiceFilters>) =>
    setFilters((prev) => ({ ...prev, ...p, page: 1 }));
  const clear = () => setFilters({ page: 1, limit: PAGE_SIZE });

  const meta = data?.meta;
  const summary = data?.summary;

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryStat
            label="Plătit"
            value={formatMoney(summary.totalPaid)}
            accent="text-green-700"
          />
          <SummaryStat
            label="În așteptare"
            value={formatMoney(summary.totalPending)}
            accent="text-amber-700"
          />
          <SummaryStat label="Venituri" value={formatMoney(summary.totalIncome)} />
          <SummaryStat label="Cheltuieli" value={formatMoney(summary.totalOutcome)} />
        </div>
      )}

      <UnifiedInvoicesFilters
        filters={filters}
        categories={categories}
        statuses={statuses}
        onChange={patch}
        onClear={clear}
      />

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Încărcarea facturilor a eșuat. Te rugăm să încerci din nou.
        </div>
      ) : isLoading && !data ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-sm">
          Se încarcă facturile…
        </div>
      ) : (
        <>
          <UnifiedInvoicesTable invoices={data?.invoices ?? []} onRowClick={setPreview} />

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Pagina {meta.page} din {meta.totalPages} · {meta.total}{' '}
                {meta.total === 1 ? 'factură' : 'facturi'}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="ui-btn-secondary"
                  disabled={meta.page <= 1}
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="ui-btn-secondary"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                >
                  Următor
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {preview && <InvoicePreviewModal invoice={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
